import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import React, { MutableRefObject } from 'react'
import { Model3dFragment } from '../src/model/fragments'
import { MeshBasicMaterial, MeshStandardMaterial } from 'three';
import classNames from 'classnames';
import { useFullscreen } from '../src/contexts/FullscreenContext';
import { useTranslate } from '../src/hooks/useTranslate';
import { render } from 'react-dom';

export type Viewport3dProps = {
	model?: Model3dFragment,
	fov?: number,
	aspectRatio?: number,
	fullscreen?: boolean
}


// - fullscreen context a schovani gallery a headeru
// - fullscreen overlay - logo presbeton, nazev produktu, krizek na zavreni
// - maly overlay - vyzva k otevreni
// - ikonka
// - chovani controls pro fullscreen
// - zkusit STL s UV jestli se nacte

const Viewport3dInner: React.FunctionComponent<Viewport3dProps> = ({ 
	model, 
	aspectRatio,
	fullscreen = false, 
	fov = 70
}) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const { isFullscreen } = useFullscreen();
	const translate = useTranslate();

	const [info, setInfo] = React.useState<string>('');
	const ref = React.useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
	React.useEffect(() => {
		const window = globalThis.window;
		if(window && ref.current && model)
		{
			let [width, height] = fullscreen ? [window.innerWidth, window.innerHeight] : [ref.current.clientWidth, ref.current.clientHeight];
			if(aspectRatio)
			{
				height = width / aspectRatio
			}

			try 
			{

			const scene = new THREE.Scene();
			scene.background = new THREE.Color(0xf2f2f2);

			const hemi = new THREE.HemisphereLight(0xffffff, 0xffffff, 1);
			scene.add(hemi);

			const camera = new THREE.PerspectiveCamera(fov, width / height, 0.01, 10);
			camera.position.set(0, 0.65, 1.7);
			camera.lookAt(0, 0, 0);
			scene.add(camera);

			const light = new THREE.PointLight(0xffffff, 20);
			light.position.set(-2 - camera.position.x, 0.55 - camera.position.y, 2 - camera.position.z);
			camera.add(light);

			const renderer = new THREE.WebGLRenderer({antialias: true});
			renderer.setSize(width, height);
			renderer.setClearColor(0xf2f2f2, 1);
			ref.current.innerHTML = '';
			ref.current.appendChild(renderer.domElement);

			// Fullscreen only
			// const controls = new OrbitControls(camera, renderer.domElement)
			// controls.minDistance = 0.5;
			// controls.maxDistance = 1.5;
			// controls.enableDamping = true;

			// Debug origin
			// const oggg = new THREE.SphereGeometry( 0.05, 32, 16 ); 
			// const omaterial = new THREE.MeshBasicMaterial( { color: 0x00ff00 } ); 
			// const origin = new THREE.Mesh( oggg, omaterial ); 
			// scene.add( origin );

			setLoading(true);
			loadScene(model.geometry.locales[0].file.url, model.geometry.locales[0].file.type, model.texture?.previewUrl!).then((group) => {
				scene.add(group);

				renderer.setAnimationLoop(() => {
					group.applyMatrix4(new THREE.Matrix4().makeRotationY(0.005));

					// controls.update()
					renderer.render(scene, camera);

					// setInfo(JSON.stringify({...renderer.info, WIDTH: width, HEIGHT: height}));
				});
			}).finally(() => setLoading(false));		
			
			return (() => {
				renderer.dispose();
			});

			} catch(e){
				setInfo((e as Error).message);
			}


		}

	}, [ref.current, fov, model, aspectRatio, isFullscreen]);

	if(!model)
	{
		return <></>;
	}

	return <div className={classNames('viewport3d', isFullscreen && 'viewport3d--fullscreen')}>
		<div className='viewport3d-overlay'>
			{
				loading && <div className='viewport3d-overlay-loader'>
					<strong dangerouslySetInnerHTML={{ __html: translate('model-3d-loading')}} />
				</div>
			}
			<span style={{fontSize: '0.8rem'}}>{ info }</span>
		</div>
		<div className='viewport3d-inner' ref={ref}>

		</div>
	</div>;
}

export const Viewport3d = Viewport3dInner;

////////////////////////////////////////////////////////
// Helpers

const loadScene = async (geometryUrl: string, geometryFormat: string,  textureUrl: string | null) : Promise<THREE.Group> => {

	let loader = null;
	let geometry : THREE.BufferGeometry | null = null;
	let group = new THREE.Group();
	switch(geometryFormat)
	{
		case 'model/stl':
			console.log('Loading STL');
			loader = new STLLoader();
			geometry = await loader.loadAsync(geometryUrl);
			break;
		case 'application/x-tgif':
			console.log('Loading OBJ');
			loader = new OBJLoader();
			const obj = await loader.loadAsync(geometryUrl);
			group.add(obj);
			break;
		case 'model/gltf-binary':
			console.log('Loading GLTF');
			loader = new GLTFLoader();
			const gltf = await loader.loadAsync(geometryUrl);
			group.add(gltf.scene);

			break;
		default:
			throw new Error(`Unknown geometry format ${geometryFormat}`);
	}

	if(geometry)
	{
		let material : MeshBasicMaterial | MeshStandardMaterial = new THREE.MeshBasicMaterial({color: 0xAAAAAA});
		if(textureUrl)
		{
			const texture = new THREE.TextureLoader().load(textureUrl); 
			texture.magFilter = THREE.NearestFilter;
			texture.anisotropy =  8;
			texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
			texture.offset.set(0, 0);
			texture.repeat.set(1,1);
			texture.needsUpdate = true;
			
			material = new THREE.MeshStandardMaterial({map: texture });
		}
	
		const mesh = new THREE.Mesh(geometry, material);
		mesh.geometry.center();

		group.add(mesh);
	}

	// Calculate bounding sphere
	const boundingSphere = calculateGroupBoundingSphere(group);
	const center  = boundingSphere.center;
	const radius = boundingSphere.radius;
	console.log('Bounding sphere', center, radius);

	// Move to origin
	group.translateX(-center.x / radius);
	group.translateY(-center.y / radius);
	group.translateZ(-center.z / radius);

	// And norm bounding sphere to 1
	group.scale.x = 1.0 / radius;
	group.scale.y = 1.0 / radius;
	group.scale.z = 1.0 / radius;

	// Debug pivot
	// const pivotGeometry = new THREE.SphereGeometry(0.3 * radius,32,16); 
	// const pivotMaterial = new THREE.MeshBasicMaterial({color: 0xff0000}); 
	// const pivot = new THREE.Mesh(pivotGeometry, pivotMaterial); 
	// pivot.position.set(boundingSphere.center.x, boundingSphere.center.y, boundingSphere.center.z);
	// group.add(pivot);

	return group;
};

const calculateGroupBoundingSphere = (group: THREE.Group) => {

	const scene = new THREE.Scene();
	scene.add(group);

	var gridHelper = new THREE.GridHelper(400, 40, 0x0000ff, 0x808080);
	gridHelper.position.y = 0;
	gridHelper.position.x = 0;
	scene.add(gridHelper);
	
	let bbox = new THREE.Box3().setFromObject(group);
	let helper = new THREE.Box3Helper(bbox, new THREE.Color(0, 255, 0));
	scene.add(helper);
	
	const center = new THREE.Vector3();
	bbox.getCenter(center);
	
	return bbox.getBoundingSphere(new THREE.Sphere(center));
};